import React from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import PpiForm from '../components/PpiForm';
import { Mode } from '../components/PpiForm/constants';
import ProgressFullscreen from '../components/common/ProgressFullscreen';
import useExcludedIngredients from '../features/IngredientContainer/hooks/useExcludedIngredients';
import useGlobalIngredients from '../features/IngredientContainer/hooks/useGlobalIngredients';
import useGlobalPpis from '../features/PpiContainer/hooks/useGlobalPpis';
import { States } from '../features/PpsiContainer/constants';
import useIngredients from '../features/IngredientContainer/hooks/useIngredients';
import useCreatePpi from '../features/PpiContainer/hooks/useCreatePpi';
import { getPpiDefaultData } from '../features/PpiContainer/util';
import { getAvailableLanguages, getLanguage } from '../features/app/selectors';

import { toJS } from '../utils/immutableUtils';
import {
  getStockHierarchyTranslated,
  getStockHierarchy
} from '../features/app/stockHierarchySelectors';
import { ppiInProgressRoute } from '../routes';
import useQueryParams from '../utils/useQueryParams';
import View from './views';

function getIngredient(...ingredientLists) {
  let ingredient = null;
  ingredientLists.forEach(ingredientList => {
    if (ingredientList.size > 0) {
      ingredient = ingredientList.first().toJS();
    }
  });
  return ingredient;
}

const PpiCreateView = () => {
  const [itemCode] = useQueryParams('itemCode');
  const [isFetchingGlobal, globalPpisRaw] = useGlobalPpis({
    status: States.PUBLISHED
  });

  // Currently the ingredients, excluded ingredients and global ingredients is
  // served by three endpoints. Each endpoint has one endpoint and there is no
  // way to know in which category it is. Hence three hooks.
  const [isFetching, ingredientsRaw] = useIngredients({
    itemCode,
    isExcluded: null
  });
  const [isFetchingExcluded, excludedIngredientsRaw] = useExcludedIngredients({
    itemCode
  });

  // const [isFetchingGlobal, globalIngredientsRaw] = useGlobalIngredients({
  //   itemCode
  // });
  const [createPpi, isSaving, createdPpiId] = useCreatePpi();
  const languages = useSelector(getAvailableLanguages);
  const currLanguage = useSelector(getLanguage);
  const firstStorageFromConfig = useSelector(getStockHierarchy).toJS();

  // GET ALL GLOBAL PPIS TO IMPORT DATA FROM
  const globalPpis = toJS(globalPpisRaw, []);

  // Redirect, if created
  if (createdPpiId) {
    return <Redirect to={ppiInProgressRoute(createdPpiId)} />;
  }

  // Render spinner
  if (isFetching || isFetchingExcluded) {
    return <ProgressFullscreen open />;
  }

  function handleCreatePpi(data, callback) {
    if (window.location.pathname.split('/').includes('create')) {
      data.parentPpiEnrichmentDataId = null;
    }
    createPpi(data, callback);
  }

  const ingredient = getIngredient(
    ingredientsRaw,
    excludedIngredientsRaw
    // globalIngredientsRaw
  );

  const getPpiRelationshipId = ingredient && ingredient.relationshipId;

  // FILTER GLOBAL PPSIS WITH PIA RELATIONSHIP ID
  const getGlobalPpiAssociated =
    globalPpis &&
    globalPpis.filter(i => i.relationshipId === getPpiRelationshipId);

  // REMOVE MULTIPLE GLOBAL INSTRUCTION WITH SINGLE PIA ARTICLE NUMBER
  const finalGlobalPpitoGetId = getGlobalPpiAssociated[0];

  const parentPpiEnrichmentDataId =
    finalGlobalPpitoGetId === undefined
      ? undefined
      : finalGlobalPpitoGetId.publishedPPIEnrichmentData.id;

  const defaultData = getPpiDefaultData(
    languages,
    ingredient,
    firstStorageFromConfig,
    currLanguage
  );

  View.PpiCreate.visit(ingredient, currLanguage);

  // Adding parentPpiEnrichmentDataId to get updates for create variant PPI also.
  const data = {
    ...defaultData,
    parentPpiEnrichmentDataId
  };

  // Render form
  return (
    <PpiForm
      defaultData={data}
      showProgress={isSaving}
      dataLabels={{
        title: `referencedIngredientData.presentationName.${currLanguage}`
      }}
      mode={Mode.CREATE}
      onSave={handleCreatePpi}
      globalPpis={globalPpis}
    />
  );
};

export default PpiCreateView;
